// =============================================================================
// FE-05: Delegation Settings Page (Admin)
// PRD §4 Feature A — all 14 org-wide delegation settings
// Route: /admin/settings/delegation
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  Bell,
  Clock,
  Users,
  RotateCcw,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

import { PageHeader } from '@/components/blocks/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { useDelegationService } from '../hooks/useDelegationService';
import type { DelegationSettings } from '../types/delegation.types';
import { usePermissions } from '@/contexts/PermissionContext';
import { ShieldOff } from 'lucide-react';
import { DEFAULT_DELEGATION_SETTINGS } from '../types/delegation.types';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SettingsSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64 mt-1" />
          </CardHeader>
          <CardContent className="space-y-5">
            {[1, 2, 3].map((j) => (
              <div key={j} className="flex items-center justify-between">
                <div className="space-y-1.5 flex-1 mr-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="h-5 w-9 rounded-full shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Setting row — toggle
// ---------------------------------------------------------------------------

interface ToggleRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (value: boolean) => void;
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  disabled,
  disabledReason,
  onChange,
}: ToggleRowProps) {
  return (
    <div
      className={`flex items-start justify-between gap-4 py-1 ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className="text-sm font-medium leading-snug cursor-pointer select-none"
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        {disabled && disabledReason && (
          <p className="text-xs text-amber-600 mt-0.5">{disabledReason}</p>
        )}
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        className="shrink-0 mt-0.5"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Setting row — number input
// ---------------------------------------------------------------------------

interface NumberRowProps {
  id: string;
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  unit?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}

function NumberRow({
  id,
  label,
  description,
  value,
  min = 1,
  max,
  unit,
  disabled,
  onChange,
}: NumberRowProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    if (!isNaN(parsed)) {
      const clamped = max !== undefined ? Math.min(parsed, max) : parsed;
      onChange(Math.max(min, clamped));
    }
  };

  return (
    <div
      className={`flex items-start justify-between gap-4 py-1 ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className="text-sm font-medium leading-snug cursor-pointer select-none"
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          className="w-20 h-8 text-sm text-center"
        />
        {unit && <span className="text-xs text-muted-foreground whitespace-nowrap">{unit}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings section card
// ---------------------------------------------------------------------------

interface SettingsSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SettingsSection({ title, description, icon, children }: SettingsSectionProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            {icon}
          </div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription className="text-xs mt-0.5">{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 divide-y divide-border/40">
        {children}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const DelegationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { service: delegationService } = useDelegationService('approval-delegation');
  const { canManageApprovalWorkflows } = usePermissions();

  // Data state
  const [settings, setSettings] = useState<DelegationSettings>(DEFAULT_DELEGATION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // ---------------------------------------------------------------------------
  // Load settings
  // ---------------------------------------------------------------------------

  const loadSettings = useCallback(async () => {
    try {
      setLoadError(null);
      setLoading(true);
      const data = await delegationService.getDelegationSettings();
      setSettings(data);
      setHasUnsavedChanges(false);
    } catch {
      setLoadError('Failed to load delegation settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [delegationService]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function patch<K extends keyof DelegationSettings>(key: K, value: DelegationSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await delegationService.updateDelegationSettings(settings);
      setSettings(updated);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      toast.success('Delegation settings saved', {
        description: 'Your changes have been applied organisation-wide.',
      });
    } catch {
      toast.error('Failed to save settings', {
        description: 'Please check your inputs and try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    }
  };

  const handleConfirmDiscard = async () => {
    setShowDiscardDialog(false);
    await loadSettings();
    toast.info('Changes discarded');
  };

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Render — access denied
  // ---------------------------------------------------------------------------

  if (!canManageApprovalWorkflows()) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <Alert variant="destructive">
          <ShieldOff className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to manage approval settings. Contact your administrator to request access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — loading
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <SettingsSkeleton />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — error
  // ---------------------------------------------------------------------------

  if (loadError) {
    return (
      <div className="space-y-6 pb-6">
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        />
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{loadError}</span>
            <Button variant="outline" size="sm" onClick={loadSettings}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — main
  // ---------------------------------------------------------------------------

  const delegationDisabled = !settings.delegationEnabled;

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-auto space-y-6 pb-24">
        {/* Page header */}
        <PageHeader
          title="Delegation Settings"

          showBreadcrumb={false}
          icon={<ShieldCheck className="h-5 w-5 text-primary" />}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings/approvals')}
              >
                Approvals Settings
              </Button>
            </div>
          }
        />

        {/* Unsaved changes banner */}
        {hasUnsavedChanges && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You have unsaved changes. Save them using the button below or they will be lost.
            </AlertDescription>
          </Alert>
        )}

        {/* ── Section 1: Master Switch ─────────────────────────────────── */}
        <Card className={`border-2 ${settings.delegationEnabled ? 'border-primary/20' : 'border-border'}`}>
          <CardContent className="pt-6 pb-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Label
                    htmlFor="delegationEnabled"
                    className="text-base font-semibold cursor-pointer select-none"
                  >
                    Enable Approval Delegation
                  </Label>
                  {settings.delegationEnabled ? (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-0 text-xs">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      Disabled
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Master switch for the entire delegation feature. When turned off, managers cannot
                  create new delegations and all delegation UI is hidden across the application.
                  Existing active delegations are not automatically revoked.
                </p>
              </div>
              <Switch
                id="delegationEnabled"
                checked={settings.delegationEnabled}
                onCheckedChange={(val) => patch('delegationEnabled', val)}
                className="shrink-0 mt-0.5"
              />
            </div>
          </CardContent>
        </Card>

        {/* Disabled overlay hint */}
        {delegationDisabled && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Delegation is currently <strong>disabled</strong> organisation-wide. The settings
              below are preserved but will not take effect until delegation is re-enabled.
            </AlertDescription>
          </Alert>
        )}

        {/* ── Section 2: Eligibility & Flow ────────────────────────────── */}
        <SettingsSection
          title="Eligibility & Flow"
          description="Control who can be a delegate and how delegations behave"
          icon={<Users className="h-4 w-4 text-primary" />}
        >
          <div className="pt-1">
            <ToggleRow
              id="eligibilityRequireApprovalPermission"
              label="Require delegate to have approval permissions"
              description="When enabled, only employees who already hold approval permissions can be selected as delegates. Recommended to keep this on for compliance."
              checked={settings.eligibilityRequireApprovalPermission}
              disabled={delegationDisabled}
              onChange={(val) => patch('eligibilityRequireApprovalPermission', val)}
            />
          </div>

          <div className="pt-3">
            <ToggleRow
              id="allowSelfDelegation"
              label="Allow self-delegation"
              description="Allow a manager to delegate approval to themselves. This should almost always remain off."
              checked={settings.allowSelfDelegation}
              disabled={delegationDisabled}
              onChange={(val) => patch('allowSelfDelegation', val)}
            />
          </div>
        </SettingsSection>

        {/* ── Section 3: Duration & Overlap Rules ─────────────────────── */}
        <SettingsSection
          title="Duration & Overlap Rules"
          description="Set limits on how long delegations can run and whether they can overlap"
          icon={<Clock className="h-4 w-4 text-primary" />}
        >
          <div className="pt-1">
            <NumberRow
              id="maxDelegationDays"
              label="Maximum delegation duration"
              description="The longest a single delegation can run. Managers cannot create delegations that exceed this limit."
              value={settings.maxDelegationDays}
              min={1}
              max={365}
              unit="days"
              disabled={delegationDisabled}
              onChange={(val) => patch('maxDelegationDays', val)}
            />
          </div>

          <div className="pt-3">
            <ToggleRow
              id="allowOverlappingDelegations"
              label="Allow overlapping delegations"
              description="When enabled, a manager can have multiple active delegations with overlapping date ranges (e.g. delegating to two people simultaneously)."
              checked={settings.allowOverlappingDelegations}
              disabled={delegationDisabled}
              onChange={(val) => patch('allowOverlappingDelegations', val)}
            />
          </div>

          <div className="pt-3">
            <ToggleRow
              id="allowCascadeDelegation"
              label="Allow cascade delegation"
              description="Allow a delegate to further delegate their inherited approval responsibilities to another person. This creates a chain of delegations."
              checked={settings.allowCascadeDelegation}
              disabled={delegationDisabled}
              onChange={(val) => patch('allowCascadeDelegation', val)}
            />
          </div>
        </SettingsSection>

        {/* ── Section 4: Form Requirements ────────────────────────────── */}
        <SettingsSection
          title="Form Requirements"
          description="Customise what managers must provide when creating a delegation"
          icon={<ShieldCheck className="h-4 w-4 text-primary" />}
        >
          <div className="pt-1">
            <ToggleRow
              id="requireNotes"
              label="Require notes on delegation"
              description="Force managers to provide a reason or notes when creating a delegation. Useful for audit trails."
              checked={settings.requireNotes}
              disabled={delegationDisabled}
              onChange={(val) => patch('requireNotes', val)}
            />
          </div>
        </SettingsSection>

        {/* ── Section 5: Notifications ─────────────────────────────────── */}
        <SettingsSection
          title="Notifications"
          description="Control which automated email notifications are sent during the delegation lifecycle"
          icon={<Bell className="h-4 w-4 text-primary" />}
        >
          <div className="pt-1">
            <ToggleRow
              id="notifyDelegateOnAssignment"
              label="Notify delegate on assignment"
              description="Send an email to the delegate when they are assigned as an approval delegate."
              checked={settings.notifyDelegateOnAssignment}
              disabled={delegationDisabled}
              onChange={(val) => patch('notifyDelegateOnAssignment', val)}
            />
          </div>

          <div className="pt-3">
            <ToggleRow
              id="notifyDelegateOnStart"
              label="Notify delegate on delegation start"
              description="Send a reminder email to the delegate on the day their delegation period begins."
              checked={settings.notifyDelegateOnStart}
              disabled={delegationDisabled}
              onChange={(val) => patch('notifyDelegateOnStart', val)}
            />
          </div>

          <div className="pt-3">
            <ToggleRow
              id="notifyManagerOnDelegateAction"
              label="Notify manager when delegate takes action"
              description="Send the manager an email each time their delegate approves or rejects a timesheet on their behalf."
              checked={settings.notifyManagerOnDelegateAction}
              disabled={delegationDisabled}
              onChange={(val) => patch('notifyManagerOnDelegateAction', val)}
            />
          </div>

          <Separator className="my-1" />

          <div className="pt-3">
            <ToggleRow
              id="notifyManagerBeforeExpiry"
              label="Notify manager before delegation expires"
              description="Send the manager a heads-up email before their delegation expires so they can renew it if needed."
              checked={settings.notifyManagerBeforeExpiry}
              disabled={delegationDisabled}
              onChange={(val) => patch('notifyManagerBeforeExpiry', val)}
            />
          </div>

          <div className="pt-3">
            <NumberRow
              id="expiryReminderHoursBefore"
              label="Expiry reminder lead time"
              description="How many hours before expiry the reminder email is sent to the manager."
              value={settings.expiryReminderHoursBefore}
              min={1}
              max={168}
              unit="hours before"
              disabled={delegationDisabled || !settings.notifyManagerBeforeExpiry}
              onChange={(val) => patch('expiryReminderHoursBefore', val)}
            />
          </div>
        </SettingsSection>

        {/* Last saved indicator */}
        {lastSaved && !hasUnsavedChanges && (
          <p className="text-xs text-muted-foreground text-center">
            Last saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
          </p>
        )}
      </div>

      {/* ── Sticky footer ────────────────────────────────────────────── */}
      <div className="shrink-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 -mx-2 sm:-mx-3 lg:-mx-3 xl:-mx-4 px-2 sm:px-3 lg:px-3 xl:px-4">
        <div className="flex h-16 items-center justify-between gap-4 max-w-[2000px] mx-auto">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {lastSaved && !hasUnsavedChanges && (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                Saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1.5 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleCancel}
              disabled={isSaving || !hasUnsavedChanges}
              className="min-w-[120px]"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Discard
            </Button>
            <Button
              size="lg"
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="min-w-[120px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Discard confirmation dialog ───────────────────────────────── */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              All unsaved changes to delegation settings will be lost and reverted to the last
              saved state. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
